import { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { PRODUCTS, CATEGORIES } from '../data/products';
import { fetchProducts } from '../services/api';
import ProductCard from '../components/ProductCard';

const SORT_OPTIONS = [
  { val: 'featured', label: 'Featured' },
  { val: 'price-asc', label: 'Price: Low to High' },
  { val: 'price-desc', label: 'Price: High to Low' },
  { val: 'rating', label: 'Highest Rated' },
];

export default function Shop() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [sort, setSort] = useState('featured');
  const [allProducts, setAllProducts] = useState(PRODUCTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts()
      .then(prods => { if (prods?.length) setAllProducts(prods); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  let filtered = activeCategory === 'all' ? allProducts : allProducts.filter(p => p.category === activeCategory);

  if (sort === 'price-asc') filtered = [...filtered].sort((a, b) => a.price - b.price);
  else if (sort === 'price-desc') filtered = [...filtered].sort((a, b) => b.price - a.price);
  else if (sort === 'rating') filtered = [...filtered].sort((a, b) => b.rating - a.rating);
  else filtered = [...filtered].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-16 lg:pt-20">
      {/* Page Hero */}
      <div className="bg-[#111111] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-14">
          <div className="section-divider" />
          <h1 data-testid="shop-heading" className="font-bebas text-5xl lg:text-6xl text-white tracking-wider mb-2">
            ALL PRODUCTS
          </h1>
          <p className="font-inter text-sm text-zinc-500">
            {filtered.length} products — Texas-made suppressors, SMEs, muzzle brakes, and clamp-ons
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Controls */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          {/* Category tabs */}
          <div data-testid="category-filters" className="flex flex-wrap gap-2">
            {[{ id: 'all', name: 'All Products' }, ...CATEGORIES].map(cat => (
              <button
                key={cat.id || cat.slug}
                data-testid={`filter-${cat.id || cat.slug}`}
                onClick={() => setActiveCategory(cat.id === 'all' ? 'all' : cat.id)}
                className={`font-bebas text-sm tracking-widest px-4 py-2 border transition-colors ${
                  activeCategory === (cat.id === 'all' ? 'all' : cat.id)
                    ? 'bg-orange-500 border-orange-500 text-white'
                    : 'border-white/10 text-zinc-400 hover:border-white/30 hover:text-white'
                }`}
              >
                {cat.name || 'All Products'}
              </button>
            ))}
          </div>

          {/* Sort */}
          <div className="relative">
            <select
              data-testid="sort-select"
              value={sort}
              onChange={e => setSort(e.target.value)}
              className="font-inter text-sm bg-[#1e1e1e] border border-white/10 text-zinc-300 pl-4 pr-8 py-2 appearance-none cursor-pointer hover:border-white/30 transition-colors"
            >
              {SORT_OPTIONS.map(o => (
                <option key={o.val} value={o.val}>{o.label}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
          </div>
        </div>

        {/* Results count */}
        <div className="mb-6">
          <span className="font-inter text-xs text-zinc-600 uppercase tracking-widest">
            Showing {filtered.length} {filtered.length === 1 ? 'product' : 'products'}
          </span>
        </div>

        {/* Product Grid */}
        <div data-testid="product-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {loading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-[#1e1e1e] border border-white/5 aspect-[4/3] animate-pulse" />
            ))
          ) : (
            filtered.map(product => (
              <ProductCard key={product.id || product._id} product={product} />
            ))
          )}
          ))}
        </div>

        {!loading && filtered.length === 0 && (
          <div className="text-center py-20">
            <p className="font-bebas text-2xl text-zinc-600 tracking-wider">NO PRODUCTS FOUND</p>
          </div>
        )}
      </div>
    </div>
  );
}
